import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import * as CartActions from '../actions/carts.action';
import { switchMap, map, catchError, tap, withLatestFrom, take, filter } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { CartsService } from '../../services/carts.service';

import { getCart, getCustomer, getCustomerAccessToken, getCustomerDefaultAddress, getCustomerTokenAndEmail } from '..';
import { Go, OpenCart, getRouterState } from 'src/app/store';
import { getActiveLanguageNoCulture } from '@teamfoster/sdk/language-ngrx';
import { Cart, CreateCustomerData, Customer, MailingAddress } from '../../models';
import { getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { Analyzify } from 'src/app/utility/analyzify.utility';
import { MetaPixel } from 'src/app/utility/meta-pixel.utility';

@Injectable()
export class CartsEffects {
  constructor(
    private actions$: Actions,
    private service: CartsService,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>,
    private store: Store
  ) {}

  initCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.InitCart),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([, lang]) => {
        if (isPlatformBrowser(this.platformId)) {
          let cartId = localStorage.getItem('CART_ID');

          if (localStorage && localStorage.getItem('CART_ID')) {
            cartId = localStorage.getItem('CART_ID');
            return this.service.FetchCart(cartId!, lang).pipe(
              map(cart => CartActions.InitCartSuccess({ cart })),
              catchError(error => of(CartActions.InitCartFail({ error })))
            );
          }
        }
        return of(CartActions.InitCartFail({ error: [] }));
      })
    );
  });

  addToCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.AddToCart),
      withLatestFrom(
        this.store.select(getCart),
        this.store.select(getCustomerTokenAndEmail),
        this.store.select(getActiveLanguageNoCulture)
      ),
      switchMap(([{ lines }, cart, token, lang]) => {
        const cartId: string | undefined = cart?.id;

        return (
          cartId ? this.service.AddLine(lines, cartId, lang) : this.service.CreateCart(lines, token.token?.accessToken, lang, token.email)
        ).pipe(
          map(c => CartActions.AddToCartSuccess({ cart: c })),
          catchError(error => {
            return of(CartActions.AddToCartFail({ error }));
          })
        );
      })
    );
  });

  addToCartSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.AddToCartSuccess),
      tap(({ cart }) => {
        let cartId = cart?.id;
        if (isPlatformBrowser(this.platformId)) {
          if (localStorage) {
            cartId = localStorage.setItem('CART_ID', cartId);
          }
        }
      }),
      map(() => OpenCart())
    );
  });

  UpdateCartLine$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.UpdateCartLine),
      withLatestFrom(this.store.select(getCart), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ lineId, quantity }, cart, lang]) => {
        if (!cart) {
          return of(CartActions.UpdateCartLineFail({ error: [{ message: 'no cart found', code: 'CUSTOM' }] }));
        }

        const cartId: string = cart.id;

        return this.service.UpdateLine(cartId, lineId, quantity, lang).pipe(
          map(c => CartActions.UpdateCartLineSuccess({ cart: c, lineId })),
          catchError(error => of(CartActions.UpdateCartLineFail({ error })))
        );
      })
    );
  });

  DeleteCartLine$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.DeleteCartLine),
      withLatestFrom(this.store.select(getCart), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ lineId }, cart, lang]) => {
        if (!cart) {
          return of(CartActions.UpdateCartLineFail({ error: [{ message: 'no cart found', code: 'CUSTOM' }] }));
        }

        const cartId = cart?.id;

        return this.service.DeleteLine(cartId, lineId, lang).pipe(
          map(c => CartActions.DeleteCartLineSuccess({ cart: c, lineId })),
          catchError(error => of(CartActions.DeleteCartLineFail({ error })))
        );
      })
    );
  });

  UpdateCartBuyerIdentity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.UpdateCartBuyerIdentity),
      withLatestFrom(this.store.select(getCart), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ token, email }, cart, lang]) => {
        if (!cart) {
          return of(CartActions.UpdateCartBuyerIdentityFail({ error: [{ message: 'no cart found', code: 'CUSTOM' }] }));
        }

        const cartId: string = cart?.id;

        return this.service.UpdateBuyerIdentity(token, cartId, lang, email).pipe(
          map(cart => CartActions.UpdateCartBuyerIdentitySuccess({ cart })),
          catchError(error => of(CartActions.UpdateCartBuyerIdentityFail({ error })))
        );
      })
    );
  });

  UpdateCartBuyerIdentitySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.UpdateCartBuyerIdentitySuccess),
      switchMap(() =>
        this.store.select(getRouterState).pipe(
          take(1),
          withLatestFrom(this.store.select(getCart), this.store.select(getCustomerDefaultAddress), this.store.select(getCustomer)),
          filter(
            ([{ state }, cart, address, customer]) =>
              state.queryParams['checkoutAfterSignin'] && JSON.parse(state.queryParams['checkoutAfterSignin']) === true
          ),
          map(([{ state }, cart, address, customer]) => {
            var checkoutUrl = cart ? buildCartCheckoutUrl(cart, address, customer) : undefined;
            return Go({
              path: checkoutUrl ? [checkoutUrl] : ['/account'],
              external: !!checkoutUrl,
            });
          })
          // map(([{ state }, cart, address, customer]) => {
          //   if (state.queryParams['checkoutAfterSignin'] && JSON.parse(state.queryParams['checkoutAfterSignin']) === true) {
          //     var checkoutUrl = cart ? buildCartCheckoutUrl(cart, address, customer) : undefined;
          //     return Go({
          //       path: [checkoutUrl] || ['/account'],
          //       external: !!checkoutUrl,
          //     });
          //   }
          //   return Go({ path: ['/account'] });
          // })
        )
      )
    );
  });

  UpdateCartNote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.UpdateCartNote),
      withLatestFrom(this.store.select(getCart), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ note }, cart, lang]) => {
        if (!cart) {
          return of(CartActions.UpdateCartNoteFail({ error: [{ message: 'no cart found', code: 'CUSTOM' }] }));
        }

        const cartId: string = cart.id;

        return this.service.UpdateNote(cartId, note, lang).pipe(
          map(c => CartActions.UpdateCartNoteSuccess({ cart: c })),
          catchError(error => of(CartActions.UpdateCartNoteFail({ error })))
        );
      })
    );
  });

  ClearCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.ClearCart),
      tap(() => {
        if (isPlatformBrowser(this.platformId)) {
          if (localStorage) {
            localStorage.removeItem('CART_ID');
          }
        }
      }),
      map(() => CartActions.ClearCartSuccess())
    );
  });

  // CheckoutLineItem
  buyNow$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.BuyNow),
      withLatestFrom(
        this.store.select(getCustomerTokenAndEmail),
        this.store.select(getActiveLanguageNoCulture),
        this.store.select(getCustomerDefaultAddress),
        this.store.select(getCustomer)
      ),
      switchMap(([{ lines }, token, lang, address, customer]) => {
        return this.service.checkoutCreate(lines, token.token?.accessToken, lang, token?.email).pipe(
          tap(a => {
            const checkoutUrl = buildCheckoutUrl(a, address, customer);
            window.location.href = checkoutUrl;
          }),
          // map(c => CartActions.BuyNowSuccess({ checkout: c })),
          catchError(error => of(CartActions.BuyNowFail({ error })))
        );
      })
    );
  });

  checkout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.StartCheckout),
        withLatestFrom(
          this.store.select(getCustomerAccessToken),
          this.store.select(getActiveLanguageNoCulture),
          this.store.select(getCart),
          this.store.select(getCustomerDefaultAddress),
          this.store.select(getCustomer),
          this.store.select(getCookiePreferences)
        ),
        tap(([_, token, lang, cart, address, customer, cookies]) => {
          if (!token || !token.accessToken) {
            this.store.dispatch(Go({ path: ['auth/inloggen'], queryParams: { checkoutAfterSignin: !!cart?.checkoutUrl } }));
          } else if (cart && cart.buyerIdentity) {
            if (cookies?.marketing) {
              Analyzify.CheckoutStart(cart);
              MetaPixel.InitiateCheckout(cart);
            }
            window.location.href = buildCartCheckoutUrl(cart, address, customer);
          }
        })
      ),
    { dispatch: false }
  );

  UpdateCartDiscount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CartActions.UpdateDiscounts),
      withLatestFrom(this.store.select(getCart), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ discountCodes, redirect }, cart, lang]) => {
        if (!cart) {
          return of(CartActions.UpdateDiscountsFail({ error: [{ message: 'no cart found', code: 'CUSTOM' }] }));
        }

        const cartId: string = cart.id;

        return this.service.cartDiscountCodesUpdate(cartId, discountCodes, lang).pipe(
          map(c => {
            //if (redirect) {
            //  return Go({ path: redirect });
            //}
            return CartActions.UpdateDiscountsSuccess({ cart: c, redirect });
          }),
          catchError(error => of(CartActions.UpdateDiscountsFail({ error, redirect })))
        );
      })
    );
  });

  UpdateCartDiscountViaUrl$ = createEffect(() => {
    return this.actions$.pipe(
      //ofType(CartActions.UpdateDiscountsSuccess),
      ofType(CartActions.UpdateDiscountsSuccess, CartActions.UpdateDiscountsFail),
      map(({ redirect }) => {
        if (redirect) {
          return Go({ path: redirect });
        }
        return Go({ path: [] });
      })
    );
  });
}

function buildCartCheckoutUrl(
  cart: Cart,
  address: MailingAddress | undefined,
  customer: CreateCustomerData | Customer | undefined
): string {
  return (
    cart.checkoutUrl +
    `&checkout[shipping_address][first_name]=${address?.firstName || ''}` +
    `&checkout[shipping_address][last_name]=${address?.lastName || ''}` +
    `&checkout[shipping_address][address1]=${address?.address1 || ''}` +
    `&checkout[shipping_address][address2]=${address?.address2 || ''}` +
    `&checkout[shipping_address][city]=${address?.city || ''}` +
    `&checkout[shipping_address][zip]=${address?.zip || ''}` +
    `&checkout[shipping_address][country]=${address?.country || ''}` +
    `&checkout[shipping_address][phone]=${address?.phone || customer?.phone || ''}` +
    `&checkout[shipping_address][company]=${address?.company || ''}` + //
    //`&checkout[billing_address][selector]=true` +
    `&checkout[billing_address][first_name]=${address?.firstName || ''}` +
    `&checkout[billing_address][last_name]=${address?.lastName || ''}` +
    `&checkout[billing_address][address1]=${address?.address1 || ''}` +
    `&checkout[billing_address][address2]=${address?.address2 || ''}` +
    `&checkout[billing_address][city]=${address?.city || ''}` +
    `&checkout[billing_address][zip]=${address?.zip || ''}` +
    `&checkout[billing_address][country]=${address?.country || ''}` +
    `&checkout[billing_address][phone]=${address?.phone || customer?.phone || ''}` +
    `&checkout[billing_address][company]=${address?.company || ''}` +
    `&checkout[email]=${customer?.email || ''}`
  );
}

function buildCheckoutUrl(checkout: any, address: MailingAddress | undefined, customer: CreateCustomerData | Customer | undefined): string {
  return (
    checkout?.url +
    `&checkout[shipping_address][first_name]=${address?.firstName || ''}` +
    `&checkout[shipping_address][last_name]=${address?.lastName || ''}` +
    `&checkout[shipping_address][address1]=${address?.address1 || ''}` +
    `&checkout[shipping_address][address2]=${address?.address2 || ''}` +
    `&checkout[shipping_address][city]=${address?.city || ''}` +
    `&checkout[shipping_address][zip]=${address?.zip || ''}` +
    `&checkout[shipping_address][country]=${address?.country || ''}` +
    `&checkout[shipping_address][phone]=${address?.phone || customer?.phone || ''}` +
    `&checkout[shipping_address][company]=${address?.company || ''}` + //
    //`&checkout[billing_address][selector]=true` +
    `&checkout[billing_address][first_name]=${address?.firstName || ''}` +
    `&checkout[billing_address][last_name]=${address?.lastName || ''}` +
    `&checkout[billing_address][address1]=${address?.address1 || ''}` +
    `&checkout[billing_address][address2]=${address?.address2 || ''}` +
    `&checkout[billing_address][city]=${address?.city || ''}` +
    `&checkout[billing_address][zip]=${address?.zip || ''}` +
    `&checkout[billing_address][country]=${address?.country || ''}` +
    `&checkout[billing_address][phone]=${address?.phone || customer?.phone || ''}` +
    `&checkout[billing_address][company]=${address?.company || ''}` +
    `&checkout[email]=${customer?.email || ''}`
  );
}
